import { useEffect, useState } from "react";
import { getAllSKUs, getSKUFieldConfig } from "../../firebase/services";
import { DEFAULT_ALL_FIELDS } from "../admin/AdminSKUs";
import { useLanguage } from "../../hooks/useLanguage";
import SkuSpecSheet from "../../components/SkuSpecSheet";

const LS_KEY = "promoterhq_sku_fields_v3";
function cachedFields() {
  try { const s = JSON.parse(localStorage.getItem(LS_KEY)); return s?.length ? s : null; }
  catch { return null; }
}

const PAGE_SIZES = [10, 50, 100];
const TC = { fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" };
const TH = { fontSize:12, fontWeight:600, background:"#C8152A", borderBottom:"2px solid #A01020", textAlign:"left", color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", padding:"8px 8px" };
const FI = { width:"100%", background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:"4px 6px", color:"#333", fontSize:11, fontFamily:"inherit", outline:"none" };
const FS = { width:"100%", background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:"4px 4px", color:"#333", fontSize:11, fontFamily:"inherit", outline:"none" };

function uniqueVals(skus, key) {
  const vals = new Set(); skus.forEach(s => { if (s[key]) vals.add(s[key]); });
  return ["", ...Array.from(vals).sort()];
}
function filterType(skus, key) {
  const vals = new Set(skus.map(s => s[key]).filter(Boolean));
  return vals.size <= 20 ? "select" : "text";
}
function colWidth(key) { return ({model:185,cpu:115,gpu:105,partNo:85,series:75,segment:70})[key]??80; }

export function PromoterSKU() {
  const { t } = useLanguage();
  const [skus, setSKUs]     = useState([]);
  const [fields, setFields] = useState(cachedFields() || DEFAULT_ALL_FIELDS);
  const [selected, setSelected] = useState(null);
  const [page, setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters]   = useState({});
  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };

  useEffect(() => {
    getAllSKUs().then(list => setSKUs(list.filter(s => s.status !== "discontinued")));
    getSKUFieldConfig().then(remote => {
      if (remote?.length) { setFields(remote); localStorage.setItem(LS_KEY, JSON.stringify(remote)); }
    });
  }, []);
  useEffect(() => { setPage(0); }, [pageSize]);

  const visibleFields = fields.filter(f => f.visible);

  const filtered = skus.filter(s => {
    for (const [key, val] of Object.entries(filters)) {
      if (!val) continue;
      if (key === "loai") {
        if (val === "highend" && !s.isHighend) return false;
        if (val === "standard" && s.isHighend) return false;
      } else if (filterType(skus, key) === "select") {
        if (s[key] !== val) return false;
      } else {
        if (!s[key]?.toLowerCase().includes(val.toLowerCase())) return false;
      }
    }
    return true;
  });
  const totalPages    = Math.ceil(filtered.length / pageSize);
  const paged         = filtered.slice(page * pageSize, (page+1)*pageSize);
  const activeFilters = Object.values(filters).filter(Boolean).length;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{t("sku.title")}</h1>
          <span style={{ fontSize:11, color:"#555" }}>
            {filtered.length!==skus.length?`${filtered.length} / ${skus.length}`:skus.length} sản phẩm
            {activeFilters>0 && <span style={{ color:"#f59e0b", marginLeft:6 }}>● {activeFilters} bộ lọc</span>}
          </span>
        </div>
        {activeFilters>0 && (
          <button className="btn btn-sm" onClick={clearFilters}>✕ {t("sku.clear_filters")}</button>
        )}
      </div>

      {/* Pagination */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#555" }}>
          {PAGE_SIZES.map(n=>(
            <button key={n} onClick={()=>setPageSize(n)}
              style={{ padding:"2px 7px", fontSize:10, borderRadius:4, border:"1px solid", cursor:"pointer",
                background:pageSize===n?"#3b82f6":"#111", borderColor:pageSize===n?"#3b82f6":"#222",
                color:pageSize===n?"#fff":"#555" }}>{n}</button>
          ))}
          <span style={{ marginLeft:2 }}>/ trang</span>
        </div>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#444" }}>
          <button className="btn btn-sm" onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{ padding:"1px 7px" }}>‹</button>
          <span>{totalPages>0?`${page+1}/${totalPages}`:"0/0"}</span>
          <button className="btn btn-sm" onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page>=totalPages-1} style={{ padding:"1px 7px" }}>›</button>
          <span style={{ color:"#333" }}>({filtered.length})</span>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX:"auto", borderRadius:8, border:"1px solid #e5e7eb" }}>
        <table className="sku-table" style={{ borderCollapse:"collapse", tableLayout:"fixed", width:"100%", minWidth:620 }}>
          <colgroup>
            {visibleFields.map(f=><col key={f.key} style={{ width:colWidth(f.key) }} />)}
            <col style={{ width:54 }} />
          </colgroup>
          <thead>
            <tr>
              {visibleFields.map(f=><th key={f.key} style={{ ...TH, ...(f.key==="model" ? { position:"sticky", left:0, zIndex:4, boxShadow:"2px 0 4px -2px rgba(0,0,0,0.25)" } : {}) }}>{f.label}</th>)}
              <th style={{ ...TH, textAlign:"center" }}>Loại</th>
            </tr>
            <tr style={{ background:"#FFF6F6", borderBottom:"1px solid #F3D6D6" }}>
              {visibleFields.map(f => {
                const ft   = filterType(skus, f.key);
                const opts = uniqueVals(skus, f.key);
                return (
                  <td key={f.key} style={f.key==="model" ? { position:"sticky", left:0, zIndex:3, background:"#FFF6F6", boxShadow:"2px 0 4px -2px rgba(0,0,0,0.15)" } : undefined}>
                    {ft === "select"
                      ? <select value={filters[f.key]||""} onChange={e=>setFilter(f.key,e.target.value)} style={FS}>
                          {opts.map(v=><option key={v} value={v}>{v||`Tất cả ${f.label}`}</option>)}
                        </select>
                      : <input value={filters[f.key]||""} onChange={e=>setFilter(f.key,e.target.value)}
                          placeholder={`Lọc ${f.label}…`} style={FI} />
                    }
                  </td>
                );
              })}
              <td>
                <select value={filters.loai||""} onChange={e=>setFilter("loai",e.target.value)} style={FS}>
                  <option value="">Tất cả</option><option value="highend">Highend</option><option value="standard">Standard</option>
                </select>
              </td>
            </tr>
          </thead>
          <tbody>
            {paged.map(s=>(
              <tr key={s.id} style={{ borderBottom:"1px solid #f0f0f0", cursor:"pointer" }} onClick={()=>setSelected(s)}>
                {visibleFields.map(f=>(
                  <td key={f.key} className={f.key==="model"?"sku-sticky":undefined}
                    style={{ padding:"6px 8px",
                      ...(f.key==="model" ? { position:"sticky", left:0, zIndex:1, boxShadow:"2px 0 4px -2px rgba(0,0,0,0.15)" } : {}) }}>
                    {f.key==="model" ? (
                      <>
                        <div style={{ ...TC, whiteSpace:"normal", wordBreak:"break-word", overflow:"visible", fontWeight:500, color:"#C8152A" }}>{s.model}</div>
                        {s.notes && <div style={{ fontSize:10, color:"#f59e0b", ...TC }} title={s.notes}>📝 {s.notes}</div>}
                      </>
                    ) : (
                      <div style={{ ...TC, color:f.key==="partNo"?"#555":f.key==="series"||f.key==="segment"?"#888":undefined }}
                        title={s[f.key]}>{s[f.key]||"—"}</div>
                    )}
                  </td>
                ))}
                <td style={{ padding:"6px 8px", textAlign:"center" }}>
                  <span className={"badge "+(s.isHighend?"badge-blue":"badge-gray")} style={{ fontSize:10, padding:"1px 5px" }}>
                    {s.isHighend?"High":"Std"}
                  </span>
                </td>
              </tr>
            ))}
            {paged.length===0 && (
              <tr><td colSpan={1+visibleFields.length} style={{ padding:20, color:"#444", textAlign:"center" }}>
                {skus.length===0?"Chưa có dữ liệu SKU.":"Không khớp bộ lọc."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail modal (read-only) */}
      {selected && (
        <div className="modal-overlay" onClick={()=>setSelected(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:600, maxHeight:"90vh", overflowY:"auto" }}>
            <div className="modal-header">
              <div className="modal-title" style={{ fontSize:14 }}>{selected.model}</div>
              <button className="btn btn-sm" onClick={()=>setSelected(null)}>✕</button>
            </div>
            <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
              <span className={"badge "+(selected.isHighend?"badge-blue":"badge-gray")} style={{ fontSize:11 }}>
                {selected.isHighend?"Highend ★":"Standard"}
              </span>
              {selected.series  && <span className="badge badge-gray" style={{ fontSize:11 }}>{selected.series}</span>}
              {selected.segment && <span className="badge badge-gray" style={{ fontSize:11 }}>{selected.segment}</span>}
            </div>
            {selected.notes && (
              <div className="alert alert-warn" style={{ fontSize:12, marginBottom:10 }}>📝 {selected.notes}</div>
            )}
            <SkuSpecSheet sku={selected} fallback={
              <table style={{ fontSize:12, width:"100%" }}><tbody>
                {fields.map(f => selected[f.key] ? (
                  <tr key={f.key}>
                    <td style={{ color:"#555", width:100, padding:"4px 0", fontSize:11, verticalAlign:"top" }}>{f.label}</td>
                    <td style={{ padding:"4px 0", wordBreak:"break-word" }}>{selected[f.key]}</td>
                  </tr>
                ) : null)}
              </tbody></table>
            } />
          </div>
        </div>
      )}
    </>
  );
}

export default PromoterSKU;
